import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Share2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { TOKEN, LINKS } from "@/lib/site";

const daysLeft = () => {
  const diff = new Date(TOKEN.countdownISO).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
};

export const ShareCard = () => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const days = daysLeft();

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const link = document.createElement("a");
      link.download = "screaming-chicken-countdown.png";
      link.href = dataUrl;
      link.click();
      toast.success("Card downloaded — go post it! 🐔");
    } catch (e) {
      toast.error("Could not generate the card. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const postOnX = () => {
    const text = `⏳ ${days} days until the SCREAMING CHICKEN ($AHHH) white paper drops.\n\nA meme with a mission — turning pump.fun hype into real crisis accommodation. #HRS #Solana #CryptoForGood`;
    const url = typeof window !== "undefined" ? window.location.origin : LINKS.dexscreener;
    const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(intent, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid="share-card-trigger"
          className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 rounded-full border border-white/20 bg-black/50 backdrop-blur-md text-white/80 hover:border-[#00f0ff] hover:text-white transition-colors"
        >
          <Share2 className="w-4 h-4" />
          Share The Countdown
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white max-w-lg" data-testid="share-card-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold uppercase tracking-tight">Share the countdown</DialogTitle>
        </DialogHeader>

        {/* The exportable card */}
        <div
          ref={cardRef}
          data-testid="share-card-visual"
          style={{
            background: "radial-gradient(120% 120% at 0% 0%, #1a0011 0%, #050505 55%)",
            borderRadius: 16,
            border: "1px solid rgba(255,0,127,0.5)",
            padding: 28,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              right: -60,
              bottom: -60,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(0,240,255,0.18)",
              filter: "blur(50px)",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <span style={{ fontFamily: "Bungee, sans-serif", fontSize: 15, color: "#fff", letterSpacing: "-0.01em" }}>
              SCREAMING CHICKEN
            </span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#39ff14", letterSpacing: "0.2em" }}>
              $AHHH
            </span>
          </div>

          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.3em", color: "#00f0ff", textTransform: "uppercase", marginBottom: 6 }}>
            White Paper Unlocks In
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 84, lineHeight: 1, color: "#fff" }}>
              {days}
            </span>
            <span style={{ fontFamily: "Bungee, sans-serif", fontSize: 24, color: "#ff007f" }}>DAYS LEFT</span>
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 14 }}>
            The blueprint drops Jan 29, 2027 · A meme with a mission.
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 22, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.4)" }}>
              SOLANA · PUMP.FUN
            </span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "#ffb800" }}>
              #EndHomelessness
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-2">
          <button
            onClick={postOnX}
            data-testid="share-post-x-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 font-display font-bold text-sm px-5 py-3 rounded-full bg-[#ff007f] text-black hover:bg-[#ffb800] transition-colors"
          >
            <Share2 className="w-4 h-4" /> Post on X
          </button>
          <button
            onClick={download}
            disabled={busy}
            data-testid="share-download-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 font-display font-bold text-sm px-5 py-3 rounded-full border border-[#00f0ff]/50 text-[#00f0ff] hover:bg-[#00f0ff] hover:text-black transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download Card
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
